import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Search, ShieldCheck, Check, X, Minus, Loader2 } from 'lucide-react';
import { rememberRegisterPreview } from '../../components/ReviewBadge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '-');
const LIGHT = { green: 'bg-emerald-500', amber: 'bg-amber-400', red: 'bg-red-500' };

const Tick = ({ v, label }) => {
  const yes = v === true || v === 'yes';
  const na = v === 'not_relevant';
  const Icon = yes ? Check : na ? Minus : X;
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className={`w-5 h-5 rounded-full flex items-center justify-center ${yes ? 'bg-emerald-100 text-emerald-700' : na ? 'bg-gray-100 text-gray-400' : 'bg-gray-100 text-gray-400'}`}><Icon className="w-3 h-3" /></span>
      <span className={yes ? 'text-gray-800' : 'text-gray-400'}>{label}{na ? ' (not relevant)' : ''}</span>
    </div>
  );
};

const Entry = ({ e }) => (
  <article className="bg-white border border-[#E5E7EB] rounded-xl p-6" data-testid={`register-entry-${e.ref}`}>
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <p className="text-xs font-mono font-semibold text-[#00857D]" data-testid={`register-ref-${e.ref}`}>CTC review ref {e.ref}</p>
        <h2 className="font-heading text-xl font-bold text-gray-900 mt-1">{e.solution_name}</h2>
        <p className="text-sm text-gray-600">by {e.company_slug ? <Link to={`/suppliers/${e.company_slug}`} className="text-[#00857D] font-semibold">{e.supplier_name}</Link> : e.supplier_name}{e.lead_contact ? ` · Lead contact: ${e.lead_contact}` : ''}</p>
      </div>
      <div className="flex items-center gap-2 border border-[#E5E7EB] rounded-full px-3 py-1.5" data-testid={`register-status-${e.ref}`}>
        <span className={`w-3 h-3 rounded-full ${LIGHT[e.status.key] || 'bg-gray-300'}`} />
        <span className="text-sm font-semibold text-gray-800">{e.status.label}</span>
      </div>
    </div>
    <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5 text-sm">
      {[['Review date', fmt(e.review_date)], ['Published', fmt(e.publish_date)], ['Next review', fmt(e.next_review_date)], ['Review tool', `${e.review_tool}\u00b0 review`]].map(([k, v]) => (
        <div key={k} className="bg-[#F4F7F9] rounded-lg px-3 py-2"><dt className="text-[11px] uppercase tracking-wider text-gray-500">{k}</dt><dd className="font-semibold text-gray-900">{v}</dd></div>
      ))}
    </dl>
    <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 mt-5">
      <Tick v={e.customer_verification} label="Customer verification" />
      <Tick v={e.road_test} label="Road test" />
      <Tick v={e.staff_feedback} label="Staff feedback" />
      <Tick v={e.stakeholder_feedback} label="Other stakeholder feedback (families, residents etc.)" />
    </div>
    <p className="text-xs text-gray-500 mt-3">Review depth: <span className="font-semibold text-gray-700">{e.depth} of 4</span> evidence sources</p>
    {(e.care_city_types?.length > 0 || e.care_city_sizes?.length > 0) && (
      <div className="mt-4 pt-4 border-t border-[#E5E7EB]">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Care City says it's for</p>
        <div className="flex flex-wrap gap-1.5">
          {[...(e.care_city_types || []), ...(e.care_city_sizes || [])].map((t) => <span key={t} className="text-xs font-medium text-indigo-700 bg-indigo-50 border border-indigo-100 px-2 py-0.5 rounded">{t}</span>)}
        </div>
      </div>
    )}
    {e.summary && <p className="text-sm text-gray-600 mt-4">{e.summary}</p>}
  </article>
);

export default function ReviewRegister() {
  const [params] = useSearchParams();
  const preview = params.get('preview');
  const [q, setQ] = useState(params.get('ref') || '');
  useEffect(() => { rememberRegisterPreview(preview); }, [preview]);
  const [data, setData] = useState(null);
  useEffect(() => {
    const t = setTimeout(() => {
      axios.get(`${API}/public/review-register`, { params: { q: q || undefined, preview: preview || undefined } })
        .then((r) => setData(r.data)).catch(() => setData(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q, preview]);

  if (data === false) return <div className="max-w-3xl mx-auto px-4 py-24 text-center text-gray-500" data-testid="register-hidden">Page not found.</div>;
  return (
    <div className="bg-[#F4F7F9] min-h-screen" data-testid="review-register-page">
      <meta name="robots" content="noindex" />
      <div className="max-w-5xl mx-auto px-4 py-12 space-y-6">
        {data && !data.live && <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-lg px-4 py-2" data-testid="register-preview-banner">Preview for sign-off: this page is hidden from the website until it's switched live.</div>}
        <div>
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#00857D] bg-[#00A79D]/10 px-3 py-1 rounded-full mb-3"><ShieldCheck className="w-3.5 h-3.5" /> CTC independently reviewed</span>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-gray-900 tracking-tight">Reviewed Products Register</h1>
          <p className="text-gray-600 mt-3 max-w-3xl">Every product here has been independently reviewed by Care Tech Compare with our assurance partner Care City. The ticks show how deep each review went, from customer verification to road tests and feedback from staff, residents and families. A green light means the review is live and in date.</p>
        </div>
        <div className="relative max-w-md">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by supplier, product or review ref"
            className="w-full bg-white border border-[#E5E7EB] rounded-md pl-9 pr-3 py-2.5 text-sm text-gray-900" data-testid="register-search" />
        </div>
        <div className="flex flex-wrap gap-4 text-xs text-gray-500">
          <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500" /> Live</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-amber-400" /> Live, re-review due within 30 days</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-red-500" /> Lapsed</span>
        </div>
        {!data ? <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
          : data.items.length === 0 ? <p className="text-gray-500" data-testid="register-empty">No reviewed products {q ? 'match your search' : 'yet'}.</p>
          : <div className="space-y-4">{data.items.map((e) => <Entry key={e.id} e={e} />)}</div>}
      </div>
    </div>
  );
}
